import styled from "styled-components";
import { Drawer, Classes } from "@blueprintjs/core";
import { Box, TitleComponent, useDarkMode } from "ui-components";
import { useEffect, useRef } from "react";

import ChatInput from "./chat-input";
import ChatMessage from "./chat-message";

import { useChat } from "./use-chat";
import {
  Footer,
  Header,
  CloseIcon,
  Messages,
  Suggestions,
  SuggestionChip,
  WelcomeText,
} from "./styles/chat.styles";
import { LoadingIndicator } from "./loading-indicator";

type Props = {
  isAIWindowOpen: boolean;
  setToggleAIWindow: () => void;
};
const AIChatWindow = (props: Props) => {
  const [darkMode] = useDarkMode();
  const { isAIWindowOpen, setToggleAIWindow } = props;
  const { messages, loading, sendMessage } = useChat();
  const chatContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = chatContainerRef.current;

    if (!container || messages.length === 0) return;

    // Use a small timeout to ensure DOM is updated with new message
    setTimeout(() => {
      const messageElements = container.querySelectorAll("[data-message-id]");
      const lastMessageElement = messageElements[messageElements.length - 1];

      if (lastMessageElement) {
        lastMessageElement.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  }, [messages]);
  return (
    <CustomDrawer
      isOpen={isAIWindowOpen}
      onClose={setToggleAIWindow}
      className={darkMode ? Classes.DARK : ""}
      isCloseButtonShown={false}
      size={"800px"}
    >
      <Header>
        <TitleComponent fontSize={"22px"}>Agent X</TitleComponent>
        <CloseIcon icon={"cross"} onClick={setToggleAIWindow} size={18} />
      </Header>

      <Messages
        ref={chatContainerRef}
        style={{
          flex: 1,
          overflowY: "auto",
          padding: "24px",
        }}
      >
        {messages.length === 0 && (
          <Box
            gap={"small"}
            items="center"
            justify="center"
            flexDir="col"
            className="h-100"
          >
            <WelcomeText>Hi there. What should we dive into today?</WelcomeText>
            <Suggestions>
              {["Create a Memo", "Analyze Industry", "Draft a Reply"].map(
                (suggestion) => (
                  <SuggestionChip key={suggestion}>{suggestion}</SuggestionChip>
                ),
              )}
            </Suggestions>
          </Box>
        )}

        {messages.map((m) => (
          <ChatMessage
            key={m.id}
            id={m.id}
            role={m.role}
            content={m.content}
            isError={m.isError}
          />
        ))}
      </Messages>

      <Footer>
        {loading && <LoadingIndicator />}
        <ChatInput onSend={sendMessage} loading={loading} />
      </Footer>
    </CustomDrawer>
  );
};

export default AIChatWindow;

const CustomDrawer = styled(Drawer)`
  border-radius: 10px;
  padding: 2px;
`;
